/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Loading from '../../components/Loading';
import axios from '../../services/axios';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';
import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { ActionBar, ActionBarRight } from '../../styles/layout/ActionBar';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';
import { EmptyState } from '../../styles/feedback/EmptyState';
import ProductNameSearch from '../../components/ProductNameSearch';

// Estilos locais
const DateChip = styled.span`
   display: inline-block;
   padding: 4px 8px;
   margin: 2px;
   border-radius: 4px;
   font-size: 11px;
   background: ${({ theme }) => theme.colors.primarySoft};
   color: ${({ theme }) => theme.colors.onPrimary};
   cursor: pointer;

   &:hover {
      opacity: 0.8;
   }
`;

const VariationBadge = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   padding: 4px 8px;
   border-radius: 4px;
   font-size: 12px;
   font-weight: 600;
   background: ${({ $isPositive }) =>
      $isPositive ? 'rgba(76, 175, 80, 0.12)' : 'rgba(244, 67, 54, 0.12)'};
   color: ${({ $isPositive }) => ($isPositive ? '#2e7d32' : '#c62828')};

   &::before {
      content: '${({ $isPositive }) => ($isPositive ? '↑' : '↓')}';
      font-size: 14px;
   }
`;

const ProductHeader = styled.div`
   background: ${({ theme }) => theme.colors.surface};
   border: 1px solid ${({ theme }) => theme.colors.border};
   border-radius: 8px;
   padding: 16px;
   margin-bottom: 20px;

   h3 {
      margin: 0 0 4px 0;
      font-size: 18px;
      color: ${({ theme }) => theme.colors.textPrimary};
   }

   p {
      margin: 0;
      font-size: 14px;
      color: ${({ theme }) => theme.colors.textMuted};
   }
`;

export default function StockComparison() {
   const [pdvs, setPdvs] = useState([]);
   const [isLoading, setIsLoading] = useState(false);

   const today = new Date()
      .toLocaleDateString('pt-BR', {
         year: 'numeric',
         month: '2-digit',
         day: '2-digit',
      })
      .split('/')
      .reverse()
      .join('-');

   // Filtros
   const [pdvId, setPdvId] = useState('');
   const [productQuery, setProductQuery] = useState('');
   const [selectedProduct, setSelectedProduct] = useState(null);
   const [selectedDates, setSelectedDates] = useState([]);
   const [dateInput, setDateInput] = useState('');

   // Resultado
   const [comparisonData, setComparisonData] = useState(null);

   useEffect(() => {
      async function loadPdvs() {
         try {
            setIsLoading(true);
            const response = await axios.get('/pdvs');
            const raw = response?.data ?? [];
            const list = Array.isArray(raw) ? raw : raw?.pdvs ?? [];
            setPdvs(Array.isArray(list) ? list : []);
         } catch (err) {
            const msg =
               err?.response?.data?.errors?.[0] ?? 'Erro ao carregar PDVs.';
            toast.error(msg);
         } finally {
            setIsLoading(false);
         }
      }

      loadPdvs();
   }, []);

   const handleAddDate = () => {
      if (!dateInput) {
         toast.error('Selecione uma data');
         return;
      }

      if (selectedDates.includes(dateInput)) {
         toast.warning('Esta data já foi adicionada');
         return;
      }

      if (selectedDates.length >= 5) {
         toast.warning('Máximo de 5 datas para comparação');
         return;
      }

      setSelectedDates([...selectedDates, dateInput].sort());
      setDateInput('');
   };

   const handleRemoveDate = (date) => {
      setSelectedDates(selectedDates.filter((d) => d !== date));
   };

   const handleClearFilters = () => {
      const ok = window.confirm('Deseja limpar todos os filtros?');
      if (!ok) return;

      setPdvId('');
      setProductQuery('');
      setSelectedProduct(null);
      setSelectedDates([]);
      setDateInput('');
      setComparisonData(null);
   };

   const handleSearch = async (e) => {
      e.preventDefault();

      if (!pdvId) {
         toast.error('Selecione um PDV');
         return;
      }

      if (selectedDates.length < 2) {
         toast.error('Selecione pelo menos 2 datas para comparar');
         return;
      }

      // Produto (SKU ou autocomplete)
      const pq = (productQuery || '').trim();
      const isDigits = /^\d+$/.test(pq);

      let prodId = null;

      if (selectedProduct?.id) {
         prodId = selectedProduct.id;
      } else if (isDigits && pq.length === 7) {
         // SKU completo - buscar produto
         try {
            const prodRes = await axios.get(`/products/sku/${pq}`);
            prodId = prodRes?.data?.id;
         } catch {
            toast.error('Produto não encontrado com este SKU');
            return;
         }
      } else {
         toast.error(
            'Selecione um produto ou digite o SKU completo (7 dígitos)'
         );
         return;
      }

      setIsLoading(true);

      try {
         const params = new URLSearchParams();
         selectedDates.forEach((date) => params.append('dates[]', date));

         const response = await axios.get(
            `/stock/comparison/${prodId}/${pdvId}?${params.toString()}`
         );

         setComparisonData(response.data);
      } catch (error) {
         const msg =
            error?.response?.data?.errors?.[0] ??
            'Erro ao comparar datas.';
         toast.error(msg);
         setComparisonData(null);
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Comparação entre Datas</PageTitle>
               <PageSubtitle>
                  Compare o estoque de um produto em diferentes datas
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid>
                     <Field $col={4} $colMobile={12}>
                        <label>PDV *</label>
                        <select
                           value={pdvId}
                           onChange={(e) => setPdvId(e.target.value)}
                           required
                        >
                           <option value="">Selecione...</option>
                           {pdvs.map((pdv) => (
                              <option key={String(pdv.id)} value={pdv.id}>
                                 {pdv.pdv_name ?? pdv.name ?? `${pdv.id}`}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={8} $colMobile={12}>
                        <label>Produto (SKU ou nome) *</label>
                        <ProductNameSearch
                           value={productQuery}
                           onChange={setProductQuery}
                           onSelect={setSelectedProduct}
                           nameLike={false}
                           displayMode="sku-name"
                           placeholder="Digite o SKU (7 dígitos) ou busque pelo nome..."
                           showHint
                        />
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label>Adicionar Data *</label>
                        <input
                           type="date"
                           value={dateInput}
                           onChange={(e) => setDateInput(e.target.value)}
                           max={today}
                        />
                     </Field>

                     <Field $col={2} $colMobile={12} $alignEnd>
                        <label>&nbsp;</label>
                        <button
                           type="button"
                           onClick={handleAddDate}
                           disabled={isLoading}
                        >
                           + Adicionar
                        </button>
                     </Field>

                     <Field $col={6} $colMobile={12}>
                        <label>
                           Datas Selecionadas ({selectedDates.length}/5)
                        </label>
                        <div style={{ minHeight: '40px', paddingTop: '8px' }}>
                           {selectedDates.length > 0 ? (
                              selectedDates.map((date) => (
                                 <DateChip
                                    key={date}
                                    onClick={() => handleRemoveDate(date)}
                                    title="Clique para remover"
                                 >
                                    {new Date(
                                       `${date  }T00:00:00`
                                    ).toLocaleDateString('pt-BR')} ✕
                                 </DateChip>
                              ))
                           ) : (
                              <p
                                 style={{
                                    margin: 0,
                                    color: '#999',
                                    fontSize: '13px',
                                 }}
                              >
                                 Nenhuma data selecionada
                              </p>
                           )}
                        </div>
                     </Field>

                     <Field $col={12} $colMobile={12} $alignEnd>
                        <label>&nbsp;</label>
                        <ActionBar>
                           <ActionBarRight>
                              <button
                                 type="button"
                                 onClick={handleClearFilters}
                                 disabled={isLoading}
                              >
                                 Limpar
                              </button>
                              <button type="submit" disabled={isLoading}>
                                 Comparar
                              </button>
                           </ActionBarRight>
                        </ActionBar>
                     </Field>
                  </FormGrid>
               </form>
            </SectionBody>
         </Section>

         {comparisonData && (
            <>
               <ProductHeader>
                  <h3>{comparisonData.snapshots[0]?.prod_name}</h3>
                  <p>
                     SKU: {comparisonData.snapshots[0]?.prod_sku} •{' '}
                     {comparisonData.snapshots[0]?.pdv_name}
                  </p>
               </ProductHeader>

               <Section>
                  <SectionHeader>
                     <SectionTitle>Snapshots</SectionTitle>
                  </SectionHeader>

                  <SectionBody>
                     <TableWrapper>
                        <Table>
                           <thead>
                              <tr>
                                 <th>Data</th>
                                 <th style={{ textAlign: 'right' }}>
                                    Estoque
                                 </th>
                              </tr>
                           </thead>
                           <tbody>
                              {comparisonData.snapshots.map((s) => (
                                 <tr key={s.snapshot_date}>
                                    <td>
                                       {new Date(
                                          `${s.snapshot_date  }T00:00:00`
                                       ).toLocaleDateString('pt-BR')}
                                    </td>
                                    <td
                                       style={{
                                          textAlign: 'right',
                                          fontWeight: 600,
                                          fontSize: '16px',
                                       }}
                                    >
                                       {s.stock_qtd} un.
                                    </td>
                                 </tr>
                              ))}
                           </tbody>
                        </Table>
                     </TableWrapper>
                  </SectionBody>
               </Section>

               {comparisonData.comparisons.length > 0 && (
                  <Section>
                     <SectionHeader>
                        <SectionTitle>Variações</SectionTitle>
                     </SectionHeader>

                     <SectionBody>
                        <TableWrapper>
                           <Table>
                              <thead>
                                 <tr>
                                    <th>Período</th>
                                    <th style={{ textAlign: 'right' }}>
                                       Estoque Inicial
                                    </th>
                                    <th style={{ textAlign: 'right' }}>
                                       Estoque Final
                                    </th>
                                    <th style={{ textAlign: 'right' }}>
                                       Variação
                                    </th>
                                    <th style={{ textAlign: 'center' }}>
                                       Variação %
                                    </th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {comparisonData.comparisons.map((c, idx) => {
                                    const isPositive = c.variation >= 0;
                                    return (
                                       <tr key={`comp-${idx}`}>
                                          <td>
                                             {new Date(
                                                `${c.from_date  }T00:00:00`
                                             ).toLocaleDateString('pt-BR')}{' '}
                                             →{' '}
                                             {new Date(
                                                `${c.to_date  }T00:00:00`
                                             ).toLocaleDateString('pt-BR')}
                                          </td>
                                          <td style={{ textAlign: 'right' }}>
                                             {c.from_stock}
                                          </td>
                                          <td style={{ textAlign: 'right' }}>
                                             {c.to_stock}
                                          </td>
                                          <td
                                             style={{
                                                textAlign: 'right',
                                                fontWeight: 600,
                                                color: isPositive
                                                   ? '#2e7d32'
                                                   : '#c62828',
                                             }}
                                          >
                                             {isPositive ? '+' : ''}
                                             {c.variation}
                                          </td>
                                          <td style={{ textAlign: 'center' }}>
                                             <VariationBadge
                                                $isPositive={isPositive}
                                             >
                                                {isPositive ? '+' : ''}
                                                {c.variation_percent}%
                                             </VariationBadge>
                                          </td>
                                       </tr>
                                    );
                                 })}
                              </tbody>
                           </Table>
                        </TableWrapper>
                     </SectionBody>
                  </Section>
               )}
            </>
         )}

         {!comparisonData && !isLoading && (
            <EmptyState
               title="Nenhuma comparação gerada"
               description="Preencha os filtros, adicione pelo menos 2 datas e clique em Comparar."
            />
         )}
      </Container>
   );
}
